<script>
  import { onMount } from "svelte";
  import Icon, { Save } from "svelte-hero-icons";
  import { headerDisplayStore } from "../stores/General";
  import Xonomy from "../libs/xonomy";
  import "../../public/assets/xonomy/xonomy.css";
  import Loader from "./Loader.svelte";
import CustomIcon from "./CustomIcon.svelte";

  let editor = false;
  let loading = false;
  let input = "";
  let showSearchAndReplaceModal = false;
  let searchText = "";
  let replaceText = "";

  export let label = "";
  export let url = "";
  export let initialXml = "";
  export let isShowSaveButton = true;

  const docSpec = {
    elements: {
      Ad: {
        attributes: {
          id: {
            asker: Xonomy.askString,
          },
        },
      },
      InLine: {
        menu: [
          {
            caption: "Append Impression",
            action: Xonomy.newElementChild,
            actionParameter: "<Impression>blank</Impression>",
          },
        ],
      },
      Impression: {
        menu: [
          {
            caption: "Delete this Impression",
            action: Xonomy.deleteElement,
          },
        ],
      },
      Creative: {
        attributes: {
          id: {
            asker: Xonomy.askString,
          },
        },
      },
      Linear: {
        menu: [
          {
            caption: "Append MediaFiles",
            action: Xonomy.newElementChild,
            actionParameter: "<MediaFiles/>",
          },
        ],
      },
      TrackingEvents: {
        menu: [
          {
            caption: "Append Tracking",
            action: Xonomy.newElementChild,
            actionParameter:
              '<Tracking event="blank" offset="00:00:00">blank</Tracking>',
          },
        ],
      },
      Tracking: {
        menu: [
          {
            caption: "Delete this Tracking",
            action: Xonomy.deleteElement,
          },
        ],
        attributes: {
          event: {
            asker: Xonomy.askPicklist,
            askerParameter: [
              { value: "firstQuartile", caption: "1st Quarter" },
              { value: "midpoint", caption: "Midpoint" },
              { value: "thirdQuartile", caption: "3rd Quarter" },
              { value: "complete", caption: "Complete" },
              { value: "start", caption: "Video Start" },
              { value: "pause", caption: "Video Pause" },
              { value: "resume", caption: "Video Resume" },
              { value: "mute", caption: "Video Mute" },
              { value: "unmute", caption: "Video Unmute" },
            ],
          },
          offset: {
            asker: Xonomy.askString,
          },
        },
      },
      VideoClicks: {
        menu: [
          {
            caption: "Append ClickThrough",
            action: Xonomy.newElementChild,
            actionParameter: "<ClickThrough>blank</ClickThrough>",
          },
          {
            caption: "Append ClickTracking",
            action: Xonomy.newElementChild,
            actionParameter: "<ClickTracking>blank</ClickTracking>",
          },
        ],
      },
      ClickThrough: {
        menu: [
          {
            caption: "Delete this ClickThrough",
            action: Xonomy.deleteElement,
          },
        ],
      },
      ClickTracking: {
        menu: [
          {
            caption: "Delete this ClickTracking",
            action: Xonomy.deleteElement,
          },
        ],
      },
      MediaFiles: {
        menu: [
          {
            caption: "Append MediaFile",
            action: Xonomy.newElementChild,
            actionParameter:
              '<MediaFile width="16" height="9" type="application/javascript" apiFramework="VPAID" delivery="progressive" bitrate="4000">blank</MediaFile>',
          },
        ],
      },
      MediaFile: {
        menu: [
          {
            caption: "Delete this MediaFile",
            action: Xonomy.deleteElement,
          },
        ],
        attributes: {
          type: {
            asker: Xonomy.askString,
          },
          width: {
            asker: Xonomy.askString,
          },
          height: {
            asker: Xonomy.askString,
          },
          apiFramework: {
            asker: Xonomy.askString,
          },
          delivery: {
            asker: Xonomy.askPicklist,
            askerParameter: [
              { value: "progressive", caption: "Progressive" },
              { value: "streaming", caption: "Streaming" },
            ],
          },
          bitrate: {
            asker: Xonomy.askString,
          },
        },
      },
      Extension: {
        attributes: {
          type: {
            asker: Xonomy.askString,
          },
        },
      },
      Verification: {
        attributes: {
          vendor: {
            asker: Xonomy.askString,
          },
        },
      },
      JavaScriptResource: {
        attributes: {
          apiFramework: {
            asker: Xonomy.askString,
          },
          browserOptional: {
            asker: Xonomy.askString,
          },
        },
      },
    },
  };

  function download(content, fileName, contentType) {
    var a = document.createElement("a");
    var file = new Blob([content], { type: contentType });
    a.href = URL.createObjectURL(file);
    a.download = fileName;
    a.click();
  }

  function searchAndReplace() {
    if (searchText) {
      loading = true;
      try {
        let xml = Xonomy.harvest();
        const editorElement = document.getElementById("editor");
        Xonomy.render(
          xml.replaceAll(searchText, replaceText),
          editorElement,
          docSpec
        );
      } catch (error) {
        headerDisplayStore.ShowError({
          title: "Error",
          subtitle: error?.message,
        });
      }
      loading = false;
    }
  }

  function handleInput(event) {
    input = event.target.value;
  }

  async function initEditor() {
    loading = true;
    try {
      if (input.substring(0, 4) === "http") {
        const response = await fetch(input, { method: "GET" });
        url = input;
        input = await response.text();
      }
      initialXml = input;
      const editorElement = document.getElementById("editor");
      Xonomy.render(input, editorElement, docSpec);
      editor = true;
    } catch (error) {
      headerDisplayStore.ShowError({
        title: "Error",
        subtitle: error?.message,
      });
    }
    loading = false;
  }

  onMount(() => {
    window.Xonomy = Xonomy;
  });
</script>

{#if label}
  <div
    class="justify-self-end block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
  >
    {label}
  </div>
{/if}
{#if !editor && !loading}
  <div class="max-w-full">
    <textarea
      on:change={handleInput}
      class="max-w-lg block w-full mb-3 shadow-sm focus:ring-orange-500 focus:border-orange-500 sm:max-w-xs sm:text-sm border-gray-300 rounded-md"
    />
    <button
      type="button"
      on:click={() => {
        initEditor();
      }}
      class="hidden sm:inline-flex -ml-px relative items-center px-4 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-900 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
    >
      <span>Edit</span>
    </button>
  </div>
{/if}
<Loader {loading} />
<div id="editor" />
{#if editor}
  <button
    type="button"
    on:click={() => {
      showSearchAndReplaceModal = true;
    }}
    class="fixed inline-flex items-center right-2 bottom-20 w-40 px-4 py-2 mr-6 rounded-md border border-gray-300 bg-yellow-500 text-sm font-medium text-yellow-800 hover:bg-yellow-400 focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
  >
    <span>Search and replace</span>
  </button>
{/if}
{#if editor && isShowSaveButton}
  <button
    type="button"
    on:click={() => {
      download(Xonomy.harvest(), "vast.xml", "text/plain");
    }}
    class="fixed inline-flex items-center right-2 bottom-2 w-28 px-4 py-2 mr-6 rounded-md border border-gray-300 bg-yellow-500 text-sm font-medium text-yellow-800 hover:bg-yellow-400 focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
  >
    <Icon src={Save} class="mr-2.5 h-5 w-5 text-white text-yellow-800" solid />
    <span>Save</span>
  </button>
{/if}
{#if showSearchAndReplaceModal}
  <div
    class="py-12  bg-gray-300 transition duration-150 ease-in-out z-10 fixed overflow-auto top-0 right-0 bottom-0 left-0"
    id="search-replace-modal"
  >
    <div
      role="alert"
      class="container mx-auto w-11/12 md:w-2/3 max-w-lg flex justify-center"
    >
      <div class="flex items-center justify-center py-8 px-4">
        <div
          class="relative md:w-96 rounded shadow-lg p-4  bg-white"
        >
          <h1
            class="text-lg font-bold leading-none  text-gray-800 pt-6 text-center"
          >
            Search and Replace
          </h1>
          <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:pt-5">
            <label
              for="modal-search"
              class="justify-self-end block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
            >
              Search
            </label>
            <div class="mt-1 sm:mt-0 sm:col-span-2">
              <input
                bind:value={searchText}
                type="text"
                id="modal-search"
                class="max-w-lg block w-full shadow-sm focus:ring-yellow-500 focus:border-yellow-500 sm:max-w-xs sm:text-sm border-gray-300 rounded-md"
              />
            </div>
          </div>
          <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:pt-5">
            <label
              for="modal-replace"
              class="justify-self-end block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
            >
              Replace
            </label>
            <div class="mt-1 sm:mt-0 sm:col-span-2">
              <input
                bind:value={replaceText}
                type="text"
                id="modal-replace"
                class="max-w-lg block w-full shadow-sm focus:ring-yellow-500 focus:border-yellow-500 sm:max-w-xs sm:text-sm border-gray-300 rounded-md"
              />
            </div>
          </div>
          <div class="px-2 pt-6 text-center">
            <button
              on:click={() => {
                showSearchAndReplaceModal = false;
                searchAndReplace();
              }}
              class="text-sm px-4 font-semibold leading-6 bg-yellow-500 rounded py-2 hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-700 text-white"
              >Update</button
            >
          </div>
          <button
            aria-label="close search-replace-modal"
            class="cursor-pointer absolute top-0 right-0 mt-0.5 mr-0.5  text-gray-400 hover:  text-gray-600 transition duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-gray-600"
            on:click={() => {
              showSearchAndReplaceModal = false;
            }}
          >
            <!-- <svg
              xmlns="http://www.w3.org/2000/svg"
              class="icon icon-tabler icon-tabler-x"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              stroke-width="2.5"
              stroke="fillColor"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path stroke="none" d="M0 0h24v24H0z" />
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg> -->

            <CustomIcon width="20"
            height="20" name="editor" color="gray"/>
          </button>
        </div>
      </div>
    </div>
  </div>
{/if}
